import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchBlogPosts, BlogPost } from '@/data/blog-posts';

function NavBar() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  return (
    <nav className="bg-[#0a0a0a] border-b border-[#222222] sticky top-0 z-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="text-lg font-semibold tracking-tight text-[#f5f5f5]">
            Custom Group
          </Link>
          <div className="hidden md:flex items-center gap-10 text-sm font-medium text-[#888888]">
            <Link to="/blog" className="text-[#f5f5f5]">Insights</Link>
            <Link to="/#approach" className="hover:text-[#f5f5f5] transition-colors">Our Approach</Link>
            <Link to="/#access" className="hover:text-[#f5f5f5] transition-colors">Advisory Access</Link>
            <Link
              to="/apply"
              className="bg-[#E8651A] text-[#0a0a0a] px-5 py-2 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
            >
              Apply for Access
            </Link>
          </div>
          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 text-[#888888] hover:text-[#f5f5f5]"
            onClick={() => setMobileMenuOpen((v) => !v)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
              </svg>
            ) : (
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            )}
          </button>
        </div>

        {/* Mobile menu */}
        {mobileMenuOpen && (
          <div className="md:hidden border-t border-[#222222] py-6 flex flex-col gap-4 text-sm font-medium">
            <Link to="/blog" className="text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Insights</Link>
            <Link to="/#approach" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Our Approach</Link>
            <Link to="/#access" className="text-[#888888] hover:text-[#f5f5f5]" onClick={() => setMobileMenuOpen(false)}>Advisory Access</Link>
            <Link
              to="/apply"
              className="bg-[#E8651A] text-[#0a0a0a] px-5 py-3 text-sm font-semibold text-center"
              onClick={() => setMobileMenuOpen(false)}
            >
              Apply for Access
            </Link>
          </div>
        )}
      </div>
    </nav>
  );
}

const ALL = 'All';

function BlogCard({ post }: { post: BlogPost }) {
  return (
    <div className="bg-[#111111] border-t border-[#222222] p-10 flex flex-col gap-6 group">
      <div className="flex items-center justify-between">
        <p className="text-xs text-[#888888] uppercase tracking-widest">{post.category}</p>
        <p className="text-xs text-[#555555]">
          {new Date(post.date).toLocaleDateString('en-ZA', {
            day: 'numeric',
            month: 'short',
            year: 'numeric',
          })}
          {' · '}{post.readTime}
        </p>
      </div>
      <h2 className="text-lg font-bold text-[#f5f5f5] leading-snug group-hover:text-[#E8651A] transition-colors">
        {post.title}
      </h2>
      <p className="text-sm text-[#888888] leading-relaxed line-clamp-3">{post.excerpt}</p>
      <Link
        to={`/blog/${post.slug}`}
        className="text-sm text-[#E8651A] hover:text-[#C9520E] transition-colors self-start"
      >
        Read →
      </Link>
    </div>
  );
}

export const BlogListPage = () => {
  const [activeCategory, setActiveCategory] = useState<string>(ALL);
  const [searchQuery, setSearchQuery] = useState('');
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    document.title = 'Insights — Custom Group AI Advisory';
    fetchBlogPosts().then(p => { setPosts(p); setLoading(false); });
  }, []);

  const categories = [ALL, ...Array.from(new Set(posts.map((p) => p.category)))];

  const filtered = posts.filter((post) => {
    const matchesCategory = activeCategory === ALL || post.category === activeCategory;
    const q = searchQuery.toLowerCase();
    const matchesSearch =
      !q ||
      post.title.toLowerCase().includes(q) ||
      post.excerpt.toLowerCase().includes(q) ||
      post.tags.some((t) => t.toLowerCase().includes(q));
    return matchesCategory && matchesSearch;
  });

  return (
    <div className="min-h-screen bg-[#0a0a0a] text-[#f5f5f5]">
      <NavBar />

      {/* Header */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <p className="text-xs text-[#E8651A] uppercase tracking-widest mb-4">Insights</p>
          <h1 className="text-4xl font-bold tracking-tight mb-4">
            Executive thinking on AI, strategy, and economic value
          </h1>
          <p className="text-[#888888] max-w-2xl leading-relaxed">
            Short, sharp analysis for leadership teams navigating AI investment, workplace transformation,
            and the shift from operational to relational work.
          </p>
        </div>
      </section>

      {/* Filters */}
      <section className="border-b border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex flex-wrap gap-2">
            {categories.map((cat) => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-1.5 text-xs font-medium tracking-wide transition-colors border ${
                  activeCategory === cat
                    ? 'border-[#E8651A] text-[#E8651A] bg-transparent'
                    : 'border-[#222222] text-[#888888] hover:border-[#444444] hover:text-[#f5f5f5]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <input
              type="search"
              placeholder="Search insights…"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full bg-[#111111] border border-[#222222] text-[#f5f5f5] px-4 py-2 text-sm focus:outline-none focus:border-[#E8651A] transition-colors placeholder-[#444444]"
            />
          </div>
        </div>
      </section>

      {/* Grid */}
      <section className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
        {filtered.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#222222]">
            {filtered.map((post) => (
              <BlogCard key={post.slug} post={post} />
            ))}
          </div>
        ) : (
          <div className="text-center py-20 text-[#555555]">
            <p className="text-base">No articles found.</p>
            <button
              onClick={() => { setActiveCategory(ALL); setSearchQuery(''); }}
              className="mt-4 text-[#E8651A] text-sm hover:text-[#C9520E]"
            >
              Clear filters
            </button>
          </div>
        )}
      </section>

      {/* Advisory CTA */}
      <section className="border-t border-[#222222]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-20">
          <div className="max-w-2xl">
            <h2 className="text-2xl font-bold mb-4 tracking-tight">
              Insights are one thing. Advisory is another.
            </h2>
            <p className="text-[#888888] mb-8 leading-relaxed">
              If your organisation is making AI investment decisions in the next 90 days,
              a 2-hour working session with our senior advisory team will save you more than it costs.
            </p>
            <Link
              to="/apply"
              className="inline-block bg-[#E8651A] text-[#0a0a0a] px-8 py-4 text-sm font-semibold hover:bg-[#C9520E] transition-colors"
            >
              Apply for Advisory Access
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-[#222222] py-16 px-6 lg:px-8">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-start gap-8">
          <div>
            <p className="text-[#f5f5f5] font-semibold mb-1">Custom Group · Ballito, South Africa</p>
            <p className="text-xs text-[#888888]">© 2026 Custom Group. All rights reserved.</p>
          </div>
          <div className="flex flex-wrap gap-8 text-sm text-[#888888]">
            <Link to="/blog" className="hover:text-[#f5f5f5] transition-colors">Insights</Link>
            <Link to="/founder" className="hover:text-[#f5f5f5] transition-colors">About</Link>
            <Link to="/apply" className="hover:text-[#f5f5f5] transition-colors">Apply</Link>
            <a
              href="https://www.linkedin.com/in/douglasbothma/"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-[#f5f5f5] transition-colors"
            >
              LinkedIn
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};
